import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { API } from '@/App';
import { 
  BrainCircuit, 
  FileText, 
  Play, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertTriangle,
  AlertCircle,
  Upload,
  RefreshCw,
  Send,
  Eye,
  ChevronDown,
  ChevronUp,
  Settings,
  BookOpen,
  GraduationCap,
  AlertOctagon,
  Trash2,
  Database,
  Globe,
  Activity,
  Shield,
  Network,
  Layers,
  BarChart3,
  Sparkles,
  Wrench
} from 'lucide-react';
import ExamConfigManager from './ExamConfigManager';
import AdminKnowledgeBase from './AdminKnowledgeBase';
// AKUViewer REMOVED - AKUs are now accessed via OntologyManager (AKU V2 sub-view)
// PipelineStatusTracker, PerformanceDashboard moved to SystemMaintenanceTab
import SourceRegistryManager from './SourceRegistryManager';
import OntologyManager from './OntologyManager';
import EvidenceViewer from './EvidenceViewer';
import RackManager from './RackManager';
import GenerateFromRack from './GenerateFromRack';
import GatesDashboard from './GatesDashboard';
import HappyPathWizard from './HappyPathWizard';
import ReviewQueueSection from './ReviewQueueSection';
import PipelineOverviewDashboard from './PipelineOverviewDashboard';
import ValidationDetailsPanel from './question_engine/ValidationDetailsPanel';

const AdminQuestionEngineTab = ({ getAuthHeaders }) => {
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState('');
  const [akus, setAkus] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [stats, setStats] = useState({ documents: 0, chunks: 0, embeddings: 0, ragReady: false });
  const [ragStats, setRagStats] = useState({ documents: 0, chunks: 0, embeddings: 0, ragReady: false, coverage_percent: 0 });
  const [pipelineRuns, setPipelineRuns] = useState([]);
  const [csvData, setCsvData] = useState('');
  const [newExamId, setNewExamId] = useState('');
  const [newExamName, setNewExamName] = useState('');
  const [runningPipeline, setRunningPipeline] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [questionDetails, setQuestionDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState('guided'); // Default to guided mode
  const [syncStatus, setSyncStatus] = useState(null);
  const [fixing, setFixing] = useState(false);
  
  // Happy Path: Advanced mode toggle
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // P1.4.1: Drilldown filter state
  const [drilldownFilter, setDrilldownFilter] = useState({ sourceId: null, filterType: null });
  
  // Generate settings
  const [maxQuestions, setMaxQuestions] = useState(10);
  const [useDistributionMatrix, setUseDistributionMatrix] = useState(true);
  const [domainDistribution, setDomainDistribution] = useState(null);
  
  // P1.4.1: Drilldown navigation handlers
  const handleNavigateToEvidence = useCallback((sourceId) => {
    setDrilldownFilter({ sourceId, filterType: 'evidence' });
    setActiveSubTab('evidence');
  }, []);
  
  const handleNavigateToChunks = useCallback((sourceId) => {
    setDrilldownFilter({ sourceId, filterType: 'chunks' });
    setActiveSubTab('evidence'); // Evidence viewer shows chunks too
  }, []);
  
  // Clear drilldown filter when manually changing tabs
  const handleTabChange = useCallback((newTab) => {
    if (newTab !== 'evidence' && newTab !== 'sources') {
      setDrilldownFilter({ sourceId: null, filterType: null });
    }
    setActiveSubTab(newTab);
  }, []);

  const fetchWithAuth = useCallback(async (endpoint, options = {}) => {
    const headers = getAuthHeaders();
    const response = await fetch(`${API}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...headers,
        ...options.headers,
      },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'Request failed');
    }
    return response.json();
  }, [getAuthHeaders]);

  const loadExams = useCallback(async () => {
    try {
      const data = await fetchWithAuth('/question-engine/exams');
      setExams(data);
      // P0 FIX: Do NOT auto-select first exam - let user choose explicitly
      // This prevents the Happy Path from starting automatically with PSM1
      // if (data.length > 0 && !selectedExam) {
      //   setSelectedExam(data[0].exam_id);
      // }
    } catch (error) {
      console.error('Failed to load exams:', error);
    }
  }, [fetchWithAuth, selectedExam]);

  const loadExamData = useCallback(async () => {
    if (!selectedExam) return;
    
    setLoading(true);
    try {
      const [akuData, questionData, statsData, runsData] = await Promise.all([
        fetchWithAuth(`/question-engine/akus?exam_id=${selectedExam}`),
        fetchWithAuth(`/question-engine/questions?exam_id=${selectedExam}`),
        fetchWithAuth(`/question-engine/stats/${selectedExam}`),
        fetchWithAuth(`/question-engine/pipeline/runs?exam_id=${selectedExam}&limit=5`),
      ]);
      
      setAkus(akuData);
      setQuestions(questionData);
      setStats(statsData);
      setPipelineRuns(runsData.runs || []);
    } catch (error) {
      console.error('Failed to load exam data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedExam, fetchWithAuth]);

  useEffect(() => {
    loadExams();
  }, [loadExams]);

  useEffect(() => {
    if (selectedExam) {
      loadExamData();
    }
  }, [selectedExam, loadExamData]);

  // Load RAG status when switching to RAG tab
  const loadRagStatus = useCallback(async () => {
    if (!selectedExam) return;
    
    try {
      const data = await fetchWithAuth(`/question-engine/rag/status/${selectedExam}`);
      setRagStats(data);
    } catch (error) {
      console.error('Failed to load RAG status:', error);
      // Set default values if endpoint fails
      setRagStats({ documents: 0, chunks: 0, embeddings: 0, ragReady: false, coverage_percent: 0 });
    }
  }, [selectedExam, fetchWithAuth]);

  // Load RAG status when tab changes to 'rag'
  useEffect(() => {
    if (activeSubTab === 'rag' && selectedExam) {
      loadRagStatus();
    }
  }, [activeSubTab, selectedExam, loadRagStatus]);

  const createExam = async () => {
    if (!newExamId || !newExamName) {
      toast.error('Bitte Exam-ID und Name eingeben');
      return;
    }
    
    try {
      await fetchWithAuth('/question-engine/exams', {
        method: 'POST',
        body: JSON.stringify({
          exam_id: newExamId.toUpperCase(),
          name: newExamName,
          version: '2024',
        }),
      });
      toast.success(`Prüfung ${newExamId} erstellt`);
      setNewExamId('');
      setNewExamName('');
      loadExams();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const importAkus = async () => {
    if (!csvData.trim()) {
      toast.error('Bitte CSV-Daten eingeben');
      return;
    }
    
    try {
      const result = await fetchWithAuth('/question-engine/akus/import-text', {
        method: 'POST',
        body: JSON.stringify({
          exam_id: selectedExam,
          csv_data: csvData,
        }),
      });
      toast.success(`${result.imported_count} AKUs importiert`);
      if (result.errors?.length > 0) {
        toast.warning(`${result.errors.length} Fehler beim Import`);
      }
      setCsvData('');
      loadExamData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const runPipeline = async () => {
    setRunningPipeline(true);
    try {
      const result = await fetchWithAuth('/question-engine/pipeline/run', {
        method: 'POST',
        body: JSON.stringify({
          exam_id: selectedExam,
          skip_validated: true,
          max_questions: maxQuestions,
          use_distribution_matrix: useDistributionMatrix,
        }),
      });
      toast.success(`Pipeline gestartet: ${result.total_akus} AKUs werden verarbeitet (max. ${maxQuestions} Fragen)`);
      
      // Poll for updates every 10 seconds
      const pollInterval = setInterval(async () => {
        await loadExamData();
        const latestRun = pipelineRuns[0];
        if (latestRun?.status === 'completed' || latestRun?.status === 'failed') {
          clearInterval(pollInterval);
          setRunningPipeline(false);
          toast.success('Pipeline abgeschlossen!');
        }
      }, 10000);
      
      // Stop polling after 10 minutes max
      setTimeout(() => {
        clearInterval(pollInterval);
        setRunningPipeline(false);
      }, 600000);
      
    } catch (error) {
      toast.error(error.message);
      setRunningPipeline(false);
    }
  };
  
  // Fetch domain distribution for the selected exam
  const fetchDomainDistribution = async () => {
    if (!selectedExam) return;
    try {
      // Use the correct endpoint: /pipelines/{exam}/config which returns eco_domains
      const data = await fetchWithAuth(`/question-engine/pipelines/${selectedExam}/config`);
      // Extract eco_domains if available
      if (data?.eco_domains) {
        setDomainDistribution(data.eco_domains);
      } else {
        setDomainDistribution(null);
      }
    } catch (error) {
      // Silently handle - domain config is optional
      setDomainDistribution(null);
    }
  };
  
  // Load domain distribution when exam changes
  useEffect(() => {
    if (selectedExam) {
      fetchDomainDistribution();
    }
  }, [selectedExam]);

  const publishQuestion = async (questionId) => {
    try {
      const result = await fetchWithAuth(`/question-engine/questions/${questionId}/publish`, {
        method: 'POST',
      });
      toast.success(`Frage als ${result.published_question_id} veröffentlicht`);
      loadExamData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const approveQuestion = async (questionId) => {
    try {
      await fetchWithAuth(`/question-engine/questions/${questionId}/approve`, {
        method: 'POST',
      });
      toast.success('Frage für Veröffentlichung freigegeben');
      loadExamData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const rejectQuestion = async (questionId) => {
    try {
      await fetchWithAuth(`/question-engine/questions/${questionId}/reject`, {
        method: 'POST',
      });
      toast.info('Frage zur manuellen Überprüfung zurückgestellt');
      loadExamData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Load detailed question data including validation results
  const loadQuestionDetails = async (questionId) => {
    setLoadingDetails(true);
    try {
      const details = await fetchWithAuth(`/question-engine/questions/${questionId}/details`);
      setQuestionDetails(details);
    } catch (error) {
      console.error('Failed to load question details:', error);
      toast.error('Fehler beim Laden der Validierungsdetails');
      setQuestionDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Toggle question details view
  const toggleQuestionDetails = async (question) => {
    if (selectedQuestion?.question_id === question.question_id) {
      setSelectedQuestion(null);
      setQuestionDetails(null);
    } else {
      setSelectedQuestion(question);
      await loadQuestionDetails(question.question_id);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      approved: { color: 'bg-green-500', icon: CheckCircle, label: 'Genehmigt' },
      rejected: { color: 'bg-red-500', icon: XCircle, label: 'Abgelehnt' },
      pending: { color: 'bg-yellow-500', icon: Clock, label: 'Ausstehend' },
      pending_review: { color: 'bg-blue-500', icon: Clock, label: 'Zu prüfen' },
      ready_for_publish: { color: 'bg-green-500', icon: CheckCircle, label: 'Bereit' },
      published: { color: 'bg-purple-500', icon: CheckCircle, label: 'Veröffentlicht' },
      manual_review: { color: 'bg-orange-500', icon: AlertTriangle, label: 'Manuell' },
      running: { color: 'bg-blue-500', icon: RefreshCw, label: 'Läuft' },
      completed: { color: 'bg-green-500', icon: CheckCircle, label: 'Abgeschlossen' },
      failed: { color: 'bg-red-500', icon: XCircle, label: 'Fehlgeschlagen' },
      regenerate: { color: 'bg-amber-500', icon: RotateCcw, label: 'Regenerieren' },
    };
    const config = variants[status] || { color: 'bg-gray-500', icon: Clock, label: status };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} text-white text-xs`}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  // Check sync status between AI questions and main collection
  const checkSyncStatus = async () => {
    try {
      const status = await fetchWithAuth('/admin/sync-status');
      setSyncStatus(status);
      return status;
    } catch (error) {
      toast.error('Fehler beim Prüfen des Sync-Status');
      return null;
    }
  };

  // Fix sync issues by force republishing
  const fixSyncIssues = async () => {
    setFixing(true);
    try {
      // First fix category mapping
      const categoryResult = await fetchWithAuth('/admin/fix-category-mapping', { method: 'POST' });
      
      // Then force republish any missing questions
      const republishResult = await fetchWithAuth('/admin/force-republish-all', { method: 'POST' });
      
      // Generate internal IDs for any questions that don't have them
      const internalIdResult = await fetchWithAuth('/admin/generate-internal-ids', { method: 'POST' });
      
      // Sync structured explanation data
      const explanationResult = await fetchWithAuth('/admin/sync-explanations', { method: 'POST' });
      
      const totalFixes = (categoryResult.fixes?.category_fixes || 0) + 
                        (republishResult.results?.republished || 0) +
                        (internalIdResult.updated || 0) +
                        (explanationResult.results?.updated || 0);
      
      if (totalFixes > 0) {
        toast.success(`${totalFixes} Probleme behoben! Kategorien: ${categoryResult.fixes?.category_fixes || 0}, Republished: ${republishResult.results?.republished || 0}, IDs: ${internalIdResult.updated || 0}, Erklärungen: ${explanationResult.results?.updated || 0}`);
      } else {
        toast.info('Keine Sync-Probleme gefunden');
      }
      
      // Refresh data
      await checkSyncStatus();
      await loadExamData();
    } catch (error) {
      toast.error('Fehler beim Beheben der Sync-Probleme: ' + error.message);
    } finally {
      setFixing(false);
    }
  };

  // Validation Details Component (extracted to question_engine/ValidationDetailsPanel.js)
  const renderValidationDetails = (details) => (
    <ValidationDetailsPanel details={details} getStatusBadge={getStatusBadge} />
  );

  return (
    <div className="space-y-6" data-testid="question-engine-tab">
      {/* Header with Exam Selector & Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="md:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Prüfung auswählen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <select 
                value={selectedExam} 
                onChange={(e) => setSelectedExam(e.target.value)}
                className="flex-1 p-2 border rounded text-sm"
                data-testid="exam-selector"
              >
                <option value="">-- Prüfung wählen --</option>
                {exams.map(exam => (
                  <option key={exam.exam_id} value={exam.exam_id}>
                    {exam.exam_id} - {exam.name}
                  </option>
                ))}
              </select>
              <Button onClick={loadExamData} variant="outline" size="sm" disabled={!selectedExam}>
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {stats && (
          <>
            <Card 
              className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-purple-500"
              onClick={() => setActiveSubTab('extract')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">AKUs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_akus}</div>
                <div className="text-xs text-gray-500">{stats.active_akus} aktiv</div>
              </CardContent>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-md transition-shadow border-l-4 border-l-blue-500"
              onClick={() => setActiveSubTab('review')}
            >
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Fragen</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_questions}</div>
                <div className="text-xs text-blue-600 font-semibold cursor-pointer hover:underline">
                  {stats.pending_review_count || 0} zu prüfen →
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-green-500">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500">Bereit / Veröff.</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.ready_for_publish_count || 0}</div>
                <div className="text-xs text-purple-600">{stats.published_questions} veröffentlicht</div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Sub-Tabs - Vollständiger Workflow */}
      <Tabs value={activeSubTab} onValueChange={handleTabChange} className="space-y-4">
        {/* Tab Header with Advanced Toggle */}
        <div className="flex items-center justify-between mb-2">
          <TabsList className="flex flex-wrap justify-start gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-lg">
            {/* Guided Mode - Always visible */}
            <TabsTrigger value="guided" className="text-xs px-3 py-1.5 data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-500 data-[state=active]:to-orange-500 data-[state=active]:text-white font-medium">
              <Sparkles className="w-3 h-3 mr-1" />
              Guided Mode
            </TabsTrigger>
            
            {/* Core workflow tabs - LOGICAL ORDER per user request (B) */}
            {/* 1. Sources */}
            <TabsTrigger value="sources" className="text-xs px-2 py-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white">
              <Database className="w-3 h-3 mr-1" />
              Sources
            </TabsTrigger>
            {/* 2. Evidence */}
            <TabsTrigger value="evidence" className="text-xs px-2 py-1.5 data-[state=active]:bg-green-500 data-[state=active]:text-white">
              <FileText className="w-3 h-3 mr-1" />
              Evidence
            </TabsTrigger>
            {/* 3. Ontology (includes Proposals, Versions, AKU V2) - CONSOLIDATED */}
            <TabsTrigger value="ontology" className="text-xs px-2 py-1.5 data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <Network className="w-3 h-3 mr-1" />
              Ontology
            </TabsTrigger>
            {/* 4. Racks - AKUs Tab REMOVED (redundant - AKUs are in Ontology) */}
            <TabsTrigger value="racks" className="text-xs px-2 py-1.5 data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
              <Layers className="w-3 h-3 mr-1" />
              Racks
            </TabsTrigger>
            {/* 5. Generate */}
            <TabsTrigger value="generate" className="text-xs px-2 py-1.5 data-[state=active]:bg-orange-500 data-[state=active]:text-white">
              <Play className="w-3 h-3 mr-1" />
              Generate
            </TabsTrigger>
            {/* 6. Validate + Gates - CONSOLIDATED */}
            <TabsTrigger value="validate" className="text-xs px-2 py-1.5 data-[state=active]:bg-red-500 data-[state=active]:text-white">
              <CheckCircle className="w-3 h-3 mr-1" />
              Validate
            </TabsTrigger>
            {/* 7. Review */}
            <TabsTrigger value="review" className="text-xs px-2 py-1.5 data-[state=active]:bg-amber-500 data-[state=active]:text-white">
              <Eye className="w-3 h-3 mr-1" />
              Review
            </TabsTrigger>
            
            {/* Advanced/Diagnostic tabs - conditionally shown */}
            {showAdvanced && (
              <>
                <TabsTrigger value="config" className="text-xs px-2 py-1.5 data-[state=active]:bg-slate-600 data-[state=active]:text-white">
                  <Settings className="w-3 h-3 mr-1" />
                  Exam Config
                </TabsTrigger>
                <TabsTrigger value="rag" className="text-xs px-2 py-1.5 data-[state=active]:bg-cyan-500 data-[state=active]:text-white">
                  <BrainCircuit className="w-3 h-3 mr-1" />
                  RAG
                </TabsTrigger>
              </>
            )}
          </TabsList>
          
          {/* Advanced Mode Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-xs text-gray-500 hover:text-gray-700"
            data-testid="advanced-mode-toggle"
          >
            {showAdvanced ? 'Standard-Ansicht' : 'Erweiterte Diagnose'}
            <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {/* Guided Mode Tab - Happy Path Wizard */}
        <TabsContent value="guided" className="space-y-4">
          {activeSubTab === 'guided' && selectedExam ? (
            <>
              {/* Pipeline Overview Dashboard */}
              <PipelineOverviewDashboard 
                examProfileId={selectedExam}
                isActive={true}
              />
              
              {/* Happy Path Wizard */}
              <HappyPathWizard 
                key={`guided-${selectedExam}`}
                isActive={true}
                examProfileId={selectedExam}
                onNavigateToTab={(tab) => setActiveSubTab(tab)}
                onNavigateToEvidence={handleNavigateToEvidence}
                onNavigateToChunks={handleNavigateToChunks}
              />
            </>
          ) : activeSubTab === 'guided' ? (
            <Card>
              <CardContent className="py-12 text-center">
                <AlertCircle className="w-12 h-12 mx-auto text-orange-500 mb-4" />
                <h3 className="text-lg font-semibold mb-2">Bitte wählen Sie zuerst ein Examen</h3>
                <p className="text-gray-500 mb-4">
                  Wählen Sie oben links ein Examen aus dem Dropdown, um den Guided Mode zu starten.
                </p>
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>

        {/* Step 3: Evidence - READ-ONLY VIEWER */}
        <TabsContent value="evidence" className="space-y-4">
          {activeSubTab === 'evidence' && (
            <EvidenceViewer 
              key={`evidence-${selectedExam}`}
              isActive={true} 
              drilldownSourceId={drilldownFilter.sourceId}
              drilldownFilterType={drilldownFilter.filterType}
              onClearDrilldown={() => setDrilldownFilter({ sourceId: null, filterType: null })}
            />
          )}
        </TabsContent>

        {/* AKUs Tab REMOVED - AKUs are now accessed via Ontology Tab (AKU V2 sub-view) */}

        {/* RAG Status Viewer - READ-ONLY (Advanced/Diagnostic) */}
        <TabsContent value="rag" className="space-y-4">
          {activeSubTab === 'rag' && (
            <>
              {/* READ-ONLY NOTICE BANNER */}
              <Card className="border-cyan-200 bg-cyan-50 dark:bg-cyan-950/20">
                <CardContent className="pt-4">
                  <div className="flex items-start gap-3">
                    <BrainCircuit className="w-5 h-5 text-cyan-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-cyan-800 dark:text-cyan-200">
                        RAG Status Viewer - Nur Einsicht
                      </p>
                      <p className="text-sm text-cyan-700 dark:text-cyan-300 mt-1">
                        RAG-Embeddings werden automatisch über <strong>Step 1 (Sources → Wizard)</strong> erstellt.
                        Diese Seite zeigt den aktuellen RAG-Status zur Einsicht.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BrainCircuit className="w-5 h-5 text-cyan-600" />
                    RAG Status für {selectedExam || 'Ihre Prüfung'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                    <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                      <p className="text-2xl font-bold text-cyan-700">{ragStats.documents || 0}</p>
                      <p className="text-sm text-cyan-600">Dokumente</p>
                    </div>
                    <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
                      <p className="text-2xl font-bold text-sky-700">{ragStats.chunks || 0}</p>
                      <p className="text-sm text-sky-600">Chunks</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-2xl font-bold text-blue-700">{ragStats.embeddings || 0}</p>
                      <p className="text-sm text-blue-600">Embeddings</p>
                    </div>
                    <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                      <p className="text-2xl font-bold text-violet-700">{ragStats.coverage_percent || 0}%</p>
                      <p className="text-sm text-violet-600">Abdeckung</p>
                    </div>
                    <div className={`p-4 rounded-lg border ${ragStats.ragReady ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                      <p className={`text-2xl font-bold ${ragStats.ragReady ? 'text-green-700' : 'text-amber-700'}`}>
                        {ragStats.ragReady ? '✓ Bereit' : '⏳ Ausstehend'}
                      </p>
                      <p className={`text-sm ${ragStats.ragReady ? 'text-green-600' : 'text-amber-600'}`}>Status</p>
                    </div>
                  </div>
                  
                  {ragStats.documents === 0 && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                      <p className="text-amber-800 text-sm">
                        <AlertTriangle className="w-4 h-4 inline mr-2" />
                        Keine Dokumente gefunden. Erstellen Sie Evidence über <strong>Step 1 (Sources → Wizard)</strong>.
                      </p>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      onClick={loadRagStatus}
                      disabled={!selectedExam}
                      data-testid="refresh-rag-status"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                      Status aktualisieren
                    </Button>
                  </div>
                  
                  {ragStats.ragReady && (
                    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-green-800 text-sm flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        RAG ist bereit! Sie können jetzt mit der Fragen-Generierung fortfahren.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Step 6: Racks - Item Families for Distractor Generation */}
        <TabsContent value="racks" className="space-y-4">
          {activeSubTab === 'racks' && (
            <RackManager 
              key={`racks-${selectedExam}`}
              examProfileId={selectedExam} 
              getAuthHeaders={getAuthHeaders}
              isActive={true}
            />
          )}
        </TabsContent>

        {/* Step 7: Question Generation */}
        <TabsContent value="generate" className="space-y-4">
          {activeSubTab === 'generate' && (
            <>
              {/* Generate from Rack - Primary Method */}
              <GenerateFromRack 
                key={`generate-${selectedExam}`}
                examProfileId={selectedExam}
                getAuthHeaders={getAuthHeaders}
                isActive={true}
              />
              
              {/* Legacy Generation Settings - Collapsed by Default */}
              <details className="mt-6">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Erweiterte Einstellungen (Legacy)
                </summary>
                <div className="mt-4 space-y-4">
          <Card className="border-orange-200 bg-gradient-to-br from-orange-50/50 to-amber-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <Play className="w-5 h-5" />
                Schritt 4: Fragen-Generierung (Legacy)
              </CardTitle>
              <CardDescription>
                Generieren Sie Prüfungsfragen aus Ihren AKUs mithilfe der Question Factory.
              </CardDescription>
            </CardHeader>
          </Card>
          
          {/* Generation Settings */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Generierungs-Einstellungen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Question Count Selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium">Anzahl Fragen</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex gap-1">
                      {[5, 10, 25, 50, 100].map(num => (
                        <button
                          key={num}
                          onClick={() => setMaxQuestions(num)}
                          className={`px-3 py-2 text-sm rounded-lg border-2 transition-all ${
                            maxQuestions === num 
                              ? 'border-orange-500 bg-orange-50 text-orange-700 font-medium' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                    <span className="text-slate-400 mx-2">oder</span>
                    <Input 
                      type="number" 
                      min="1" 
                      max="500"
                      value={maxQuestions}
                      onChange={(e) => setMaxQuestions(parseInt(e.target.value) || 10)}
                      className="w-20"
                    />
                    <span className="text-sm text-slate-500">Fragen</span>
                  </div>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Verteilungs-Strategie</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={useDistributionMatrix}
                        onChange={(e) => setUseDistributionMatrix(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      <span className="text-sm">Verteilungsmatrix verwenden</span>
                    </label>
                    {useDistributionMatrix && (
                      <Badge className="bg-green-100 text-green-700">Aktiviert</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Fragen werden proportional auf alle Domains/Kategorien verteilt
                  </p>
                </div>
              </div>
              
              {/* Distribution Matrix Preview */}
              {useDistributionMatrix && domainDistribution && domainDistribution.domains && (
                <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">
                    Verteilungsmatrix für {selectedExam}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {domainDistribution.domains.map((domain, idx) => {
                      const questionsForDomain = Math.round((domain.percentage / 100) * maxQuestions);
                      return (
                        <div key={idx} className="p-2 bg-white rounded border text-center">
                          <div className="text-xs text-slate-500 truncate" title={domain.name}>
                            {domain.name}
                          </div>
                          <div className="text-lg font-bold text-orange-600">
                            {questionsForDomain}
                          </div>
                          <div className="text-xs text-slate-400">
                            ({domain.percentage}%)
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    Gesamt: {maxQuestions} Fragen über {domainDistribution.domains.length} Domains
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Primary Action: Start AI Pipeline */}
          <Card className="border-2 border-orange-300 bg-gradient-to-r from-orange-100 to-amber-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-orange-500 rounded-full">
                    <BrainCircuit className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-orange-800">AI Question Factory</h3>
                    <p className="text-sm text-orange-700">
                      {akus.length} AKUs verfügbar • {maxQuestions} Fragen • RAG + 5-fache Validierung
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={runPipeline} 
                  disabled={runningPipeline || akus.length === 0 || !selectedExam}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-6 text-lg"
                  size="lg"
                  data-testid="run-pipeline-btn-main"
                >
                  {runningPipeline ? (
                    <>
                      <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                      Generiere...
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5 mr-2" />
                      {maxQuestions} Fragen generieren
                    </>
                  )}
                </Button>
              </div>
              {akus.length === 0 && (
                <div className="mt-4 p-3 bg-amber-100 border border-amber-300 rounded-lg text-sm text-amber-800">
                  ⚠️ Keine AKUs vorhanden. Importieren Sie zuerst AKUs aus dem Tab "2. AKUs" oder über das Formular unten.
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Pipeline Run Status */}
          {pipelineRuns.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-gray-500" />
                  Letzte Pipeline-Läufe
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {pipelineRuns.slice(0, 3).map(run => (
                    <div key={run.run_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge variant={run.status === 'completed' ? 'default' : run.status === 'running' ? 'secondary' : 'destructive'}>
                          {run.status}
                        </Badge>
                        <span className="text-sm font-mono">{run.run_id}</span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {run.questions_generated || 0} Fragen • {run.total_akus || 0} AKUs
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* AKU Import - Secondary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">AKUs aus CSV importieren</CardTitle>
              <CardDescription>
                Format: statement,category,type,section,complexity,risk_level
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="statement,category,type,section,complexity,risk_level
Der Scrum Master ist verantwortlich für...,Scrum Master Role,accountability,Scrum Team,medium,high"
                value={csvData}
                onChange={(e) => setCsvData(e.target.value)}
                rows={6}
                className="font-mono text-sm"
                data-testid="csv-input"
              />
              <Button onClick={importAkus} disabled={!selectedExam || !csvData.trim()}>
                <Upload className="w-4 h-4 mr-2" />
                AKUs importieren
              </Button>
            </CardContent>
          </Card>

          {akus.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Importierte AKUs ({akus.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {akus.map(aku => (
                    <div key={aku.aku_id} className="p-3 border rounded-lg text-sm">
                      <p>{aku.statement}</p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline">{aku.category || 'N/A'}</Badge>
                        <Badge variant="secondary">{aku.complexity}</Badge>
                        {aku.questions_generated > 0 && (
                          <span className="text-xs text-green-600">{aku.questions_generated} Fragen</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
            </div>
          </details>
            </>
          )}
        </TabsContent>
        
        {/* Step 5: Validate Tab - Validation Pipeline + Quality Gates (CONSOLIDATED) */}
        <TabsContent value="validate" className="space-y-4">
          {activeSubTab === 'validate' && (
            <>
              <Card className="border-red-200 bg-gradient-to-br from-red-50/50 to-rose-50/50">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <CheckCircle className="w-5 h-5" />
                    Schritt 5: Validierung & Quality Gates
                  </CardTitle>
                  <CardDescription>
                    5-fache Validierungs-Pipeline und Quality Gates zur Qualitätssicherung
                  </CardDescription>
                </CardHeader>
              </Card>
              
              {/* Validation Pipeline Overview */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600" />
                    Validierungs-Pipeline
                  </CardTitle>
                  <CardDescription>
                    Jede Frage durchläuft 5 Validierungsstufen bevor sie genehmigt wird
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-5 gap-2 mb-6">
                    <div className="p-3 bg-cyan-50 rounded-lg border border-cyan-200 text-center">
                      <BrainCircuit className="w-6 h-6 mx-auto text-cyan-600 mb-1" />
                      <p className="text-xs font-medium">RAG Evidence</p>
                      <p className="text-[10px] text-gray-500">KB-Prüfung</p>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-center">
                      <BookOpen className="w-6 h-6 mx-auto text-blue-600 mb-1" />
                      <p className="text-xs font-medium">Academic</p>
                      <p className="text-[10px] text-gray-500">Normativ</p>
                    </div>
                    <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 text-center">
                      <AlertTriangle className="w-6 h-6 mx-auto text-orange-600 mb-1" />
                      <p className="text-xs font-medium">Distractor</p>
                      <p className="text-[10px] text-gray-500">Antworten</p>
                    </div>
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-center">
                      <GraduationCap className="w-6 h-6 mx-auto text-purple-600 mb-1" />
                      <p className="text-xs font-medium">Didactic</p>
                      <p className="text-[10px] text-gray-500">Pädagogik</p>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200 text-center">
                      <FileText className="w-6 h-6 mx-auto text-green-600 mb-1" />
                      <p className="text-xs font-medium">Semantic</p>
                      <p className="text-[10px] text-gray-500">Konsistenz</p>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-slate-50 rounded-lg border">
                    <h4 className="font-medium mb-3">Scoring-System:</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span><strong>≥85 Punkte:</strong> Auto-Genehmigung</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                        <span><strong>60-84 Punkte:</strong> Review-Queue</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <span><strong>&lt;60 Punkte:</strong> Ablehnung</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Quality Gates Dashboard - INTEGRATED */}
              <Card className="border-emerald-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5 text-emerald-600" />
                    Quality Gates Dashboard
                  </CardTitle>
                  <CardDescription>
                    S1/S2 (Source Gates) und V1-V4 (Validation Gates) für den gesamten Workflow
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <GatesDashboard key="gates-in-validate" isActive={true} />
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Step 6: Review Tab - Review Queue */}
        <TabsContent value="review" className="space-y-4">
          {/* NEW: Happy Path Review Queue - Primary for new workflow */}
          <ReviewQueueSection 
            getAuthHeaders={getAuthHeaders} 
            selectedExam={selectedExam}
          />
          
          {/* Separator between new and legacy systems */}
          <div className="my-6 border-t-2 border-dashed border-gray-300 pt-4">
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              <span>Legacy AI Question Factory (unten) - wird durch Happy Path ersetzt</span>
            </p>
          </div>
          
          {/* LEGACY: Original Review System for ai_questions collection */}
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50/50 to-yellow-50/50">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <Eye className="w-5 h-5" />
                Legacy: AI Question Factory Review
              </CardTitle>
              <CardDescription>
                Älteres System für ai_questions Collection (wird schrittweise ersetzt)
              </CardDescription>
            </CardHeader>
          </Card>
          
          {/* Quick Stats for Review */}
          <div className="grid grid-cols-4 gap-4">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-full">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-700">{stats?.pending_review_count || 0}</div>
                    <div className="text-xs text-blue-600">Zu prüfen</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-green-50 border-green-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-full">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-700">{stats?.ready_for_publish_count || 0}</div>
                    <div className="text-xs text-green-600">Bereit</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-full">
                    <Send className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-700">{stats?.published_questions || 0}</div>
                    <div className="text-xs text-purple-600">Veröffentlicht</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-full">
                    <XCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-700">{stats?.rejected_count || 0}</div>
                    <div className="text-xs text-red-600">Abgelehnt</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Sync Status Card */}
          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <RefreshCw className="w-4 h-4" />
                  Sync-Status (AI Engine → Question Management)
                </CardTitle>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={checkSyncStatus}
                    disabled={fixing}
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Prüfen
                  </Button>
                  <Button 
                    size="sm" 
                    variant="default"
                    onClick={fixSyncIssues}
                    disabled={fixing}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {fixing ? (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                        Behebe...
                      </>
                    ) : (
                      <>
                        <AlertOctagon className="w-3 h-3 mr-1" />
                        Probleme beheben
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardHeader>
            {syncStatus && (
              <CardContent className="pt-0">
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                    <div className="font-bold text-lg text-purple-600">{syncStatus.published_ai_questions}</div>
                    <div className="text-xs text-gray-500">Veröffentlicht (AI)</div>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                    <div className="font-bold text-lg text-green-600">{syncStatus.correctly_synced}</div>
                    <div className="text-xs text-gray-500">Korrekt synchronisiert</div>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                    <div className={`font-bold text-lg ${syncStatus.sync_issues > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                      {syncStatus.sync_issues}
                    </div>
                    <div className="text-xs text-gray-500">Sync-Probleme</div>
                  </div>
                  <div className="text-center p-2 bg-white dark:bg-gray-800 rounded">
                    <div className="font-bold text-lg text-blue-600">{syncStatus.ai_generated_in_main}</div>
                    <div className="text-xs text-gray-500">Im Question Mgmt</div>
                  </div>
                </div>
                {syncStatus.sync_issues > 0 && (
                  <p className="text-xs text-red-600 mt-2">
                    ⚠️ Es gibt {syncStatus.sync_issues} Sync-Probleme. Klicken Sie auf "Probleme beheben" um diese zu reparieren.
                  </p>
                )}
              </CardContent>
            )}
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Generierte Fragen ({questions.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {questions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  Noch keine Fragen generiert. Importiere AKUs und starte die Pipeline.
                </p>
              ) : (
                <div className={`space-y-3 overflow-y-auto ${selectedQuestion ? 'max-h-none' : 'max-h-[500px]'}`}>
                  {questions.map(q => (
                    <div key={q.question_id} className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex items-center gap-2">
                          {/* Display internal_id as primary identifier */}
                          <span className="font-mono text-sm font-semibold text-blue-600 dark:text-blue-400">
                            {q.internal_id || q.question_id}
                          </span>
                          {/* Show technical ID if different */}
                          {q.internal_id && (
                            <span className="font-mono text-xs text-gray-400">
                              ({q.question_id})
                            </span>
                          )}
                        </div>
                        {getStatusBadge(q.publish_status)}
                      </div>
                      
                      <p className="text-sm mb-3">
                        {(q.scenario ? `[Szenario] ${q.scenario.substring(0, 80)}... ` : '')}
                        {(q.question_text || q.raw_question_json?.question_text || '').substring(0, 150)}...
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-3">
                        <Badge variant="outline" className="text-xs">
                          Akademisch: {q.academic_status}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          Didaktisch: {q.didactic_status}
                        </Badge>
                        {q.retry_count > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            {q.retry_count} Retries
                          </Badge>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => toggleQuestionDetails(q)}
                          disabled={loadingDetails && selectedQuestion?.question_id === q.question_id}
                        >
                          {loadingDetails && selectedQuestion?.question_id === q.question_id ? (
                            <><RefreshCw className="w-4 h-4 mr-1 animate-spin" />Laden...</>
                          ) : selectedQuestion?.question_id === q.question_id ? (
                            <><ChevronUp className="w-4 h-4 mr-1" />Schließen</>
                          ) : (
                            <><Eye className="w-4 h-4 mr-1" />Details</>
                          )}
                        </Button>
                        {/* Workflow Buttons based on status */}
                        {q.publish_status === 'pending_review' && (
                          <>
                            <Button 
                              size="sm"
                              variant="default"
                              onClick={() => approveQuestion(q.question_id)}
                              data-testid={`approve-${q.question_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Freigeben
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => rejectQuestion(q.question_id)}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Zurückweisen
                            </Button>
                            <Button 
                              size="sm"
                              variant="destructive"
                              onClick={async () => {
                                if (!window.confirm('Frage wirklich permanent löschen?')) return;
                                try {
                                  await fetchWithAuth(`/question-engine/questions/${q.question_id}`, { method: 'DELETE' });
                                  toast.success('Frage gelöscht');
                                  loadExamData();
                                } catch (error) {
                                  toast.error(error.message);
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Löschen
                            </Button>
                          </>
                        )}
                        {q.publish_status === 'manual_review' && (
                          <>
                            <Button 
                              size="sm"
                              variant="secondary"
                              onClick={async () => {
                                try {
                                  await fetchWithAuth(`/question-engine/questions/${q.question_id}/manual-approve`, {
                                    method: 'POST',
                                  });
                                  toast.success('Frage manuell genehmigt');
                                  loadExamData();
                                } catch (error) {
                                  toast.error(error.message);
                                }
                              }}
                              data-testid={`manual-approve-${q.question_id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Manuell genehmigen
                            </Button>
                            <Button 
                              size="sm"
                              variant="destructive"
                              onClick={async () => {
                                if (!window.confirm('Frage wirklich permanent löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
                                  return;
                                }
                                try {
                                  await fetchWithAuth(`/question-engine/questions/${q.question_id}`, {
                                    method: 'DELETE',
                                  });
                                  toast.success('Frage wurde permanent gelöscht');
                                  loadExamData();
                                } catch (error) {
                                  toast.error(error.message);
                                }
                              }}
                              data-testid={`delete-${q.question_id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Löschen
                            </Button>
                          </>
                        )}
                        {q.publish_status === 'ready_for_publish' && (
                          <>
                            <Button 
                              size="sm"
                              onClick={() => publishQuestion(q.question_id)}
                              data-testid={`publish-${q.question_id}`}
                            >
                              <Send className="w-4 h-4 mr-1" />
                              Veröffentlichen
                            </Button>
                            <Button 
                              size="sm"
                              variant="outline"
                              onClick={() => rejectQuestion(q.question_id)}
                              data-testid={`reject-ready-${q.question_id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Zurückweisen
                            </Button>
                            <Button 
                              size="sm"
                              variant="destructive"
                              onClick={async () => {
                                if (!window.confirm('Frage wirklich permanent löschen? Diese Aktion kann nicht rückgängig gemacht werden.')) {
                                  return;
                                }
                                try {
                                  await fetchWithAuth(`/question-engine/questions/${q.question_id}`, {
                                    method: 'DELETE',
                                  });
                                  toast.success('Frage wurde permanent gelöscht');
                                  loadExamData();
                                } catch (error) {
                                  toast.error(error.message);
                                }
                              }}
                              data-testid={`delete-ready-${q.question_id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Löschen
                            </Button>
                          </>
                        )}
                        {/* Published questions can also be deleted from AI Engine */}
                        {q.publish_status === 'published' && (
                          <Button 
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            onClick={async () => {
                              if (!window.confirm('AI-Frage aus der Engine UND aus dem Question Management entfernen? Diese Aktion kann nicht rückgängig gemacht werden!')) {
                                return;
                              }
                              try {
                                await fetchWithAuth(`/question-engine/questions/${q.question_id}?force=true`, {
                                  method: 'DELETE',
                                });
                                toast.success('AI-Frage wurde vollständig gelöscht');
                                loadExamData();
                              } catch (error) {
                                toast.error(error.message);
                              }
                            }}
                            data-testid={`delete-published-${q.question_id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Vollständig löschen
                          </Button>
                        )}
                      </div>

                      {/* Expanded Details */}
                      {selectedQuestion?.question_id === q.question_id && (
                        <div className="mt-4 pt-4 border-t space-y-3">
                          {/* Question Content Section */}
                          <div className="space-y-3">
                            {/* Scenario */}
                            {(q.scenario || q.raw_question_json?.scenario) && (
                              <div>
                                <Label className="text-xs text-gray-500">Szenario:</Label>
                                <p className="mt-1 p-2 bg-blue-50 dark:bg-blue-900/20 rounded text-sm">
                                  {q.scenario || q.raw_question_json?.scenario}
                                </p>
                              </div>
                            )}
                            
                            <div>
                              <Label className="text-xs text-gray-500">Frage:</Label>
                              <p className="mt-1 p-2 bg-gray-50 dark:bg-gray-800 rounded text-sm">
                                {q.question_text || q.raw_question_json?.question_text}
                              </p>
                            </div>
                            
                            <div>
                              <Label className="text-xs text-gray-500">Antworten:</Label>
                              <div className="mt-1 space-y-1">
                                {(() => {
                                  const answers = q.answers || q.raw_question_json?.answers || q.raw_question_json?.choices || [];
                                  const correctAnswers = q.correct_answers || q.raw_question_json?.correct_answers || [];
                                  
                                  // Handle both Dict format {"A": "text"} and Array format [{id: "A", text: "..."}]
                                  if (Array.isArray(answers)) {
                                    return answers.map(c => (
                                      <div 
                                        key={c.id || c}
                                        className={`p-2 rounded text-sm ${
                                          correctAnswers.includes(c.id)
                                            ? 'bg-green-100 dark:bg-green-900/30 border border-green-300'
                                            : 'bg-gray-50 dark:bg-gray-800'
                                        }`}
                                      >
                                        <strong>{c.id}:</strong> {c.text}
                                      </div>
                                    ));
                                  } else if (typeof answers === 'object') {
                                    return Object.entries(answers).map(([key, value]) => (
                                      <div 
                                        key={key}
                                        className={`p-2 rounded text-sm ${
                                          correctAnswers.includes(key)
                                            ? 'bg-green-100 dark:bg-green-900/30 border border-green-300'
                                            : 'bg-gray-50 dark:bg-gray-800'
                                        }`}
                                      >
                                        <strong>{key}:</strong> {value}
                                      </div>
                                    ));
                                  }
                                  return <p className="text-gray-500 text-sm">Keine Antworten verfügbar</p>;
                                })()}
                              </div>
                            </div>
                            
                            {/* Correct Justification */}
                            <div>
                              <Label className="text-xs text-gray-500">Begründung (korrekte Antwort):</Label>
                              <p className="mt-1 p-2 bg-green-50 dark:bg-green-900/20 rounded text-xs">
                                {q.correct_justification || q.raw_question_json?.correct_justification || q.raw_question_json?.explanation}
                              </p>
                            </div>
                            
                            {/* Incorrect Explanations */}
                            {Object.keys(q.incorrect_explanations || q.raw_question_json?.incorrect_explanations || {}).length > 0 && (
                              <div>
                                <Label className="text-xs text-gray-500">Erklärungen (falsche Antworten):</Label>
                                <div className="mt-1 space-y-1">
                                  {Object.entries(q.incorrect_explanations || q.raw_question_json?.incorrect_explanations || {}).map(([key, value]) => (
                                    <div key={key} className="p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs">
                                      <strong>{key}:</strong> {value}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {/* Reference */}
                            {(q.reference || q.raw_question_json?.reference) && (
                              <div>
                                <Label className="text-xs text-gray-500">Referenz:</Label>
                                <p className="mt-1 p-2 bg-gray-100 dark:bg-gray-700 rounded text-xs">
                                  <strong>Quelle:</strong> {(q.reference || q.raw_question_json?.reference)?.source || 'N/A'}<br/>
                                  <strong>Abschnitt:</strong> {(q.reference || q.raw_question_json?.reference)?.section || 'N/A'}
                                </p>
                              </div>
                            )}
                          </div>
                          
                          {/* Separator */}
                          <Separator className="my-4" />
                          
                          {/* Validation Details Section */}
                          <div>
                            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
                              <BrainCircuit className="w-4 h-4" />
                              Validierungs-Details (Qualitätskontrolle)
                            </h4>
                            
                            {loadingDetails ? (
                              <div className="flex items-center justify-center py-8">
                                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
                                <span className="ml-2 text-gray-500">Lade Validierungsdaten...</span>
                              </div>
                            ) : questionDetails?.validation_details ? (
                              renderValidationDetails(questionDetails)
                            ) : (
                              <p className="text-sm text-gray-500 py-4 text-center">
                                Keine Validierungsdaten verfügbar
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Create Exam Tab */}
        <TabsContent value="create" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Neue Prüfung erstellen
              </CardTitle>
              <CardDescription>
                Erstelle eine neue Zertifizierungsprüfung für die Question Engine
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Exam-ID (z.B. PMP, CAPM, PSM, PSPO)</Label>
                  <Input 
                    value={newExamId}
                    onChange={(e) => setNewExamId(e.target.value.toUpperCase())}
                    placeholder="PSM"
                    className="mt-1"
                    data-testid="new-exam-id"
                  />
                </div>
                <div>
                  <Label>Vollständiger Name</Label>
                  <Input 
                    value={newExamName}
                    onChange={(e) => setNewExamName(e.target.value)}
                    placeholder="Professional Scrum Master"
                    className="mt-1"
                    data-testid="new-exam-name"
                  />
                </div>
              </div>
              <Button onClick={createExam} disabled={!newExamId || !newExamName}>
                <FileText className="w-4 h-4 mr-2" />
                Prüfung erstellen
              </Button>
            </CardContent>
          </Card>

          {exams.length > 0 && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Vorhandene Prüfungen ({exams.length})</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        const response = await fetch(`${API}/admin/fix-ai-engine-exam-names`, {
                          method: 'POST',
                          headers: getAuthHeaders()
                        });
                        if (response.ok) {
                          const data = await response.json();
                          if (data.fixed_count > 0) {
                            toast.success(`${data.fixed_count} Prüfungsnamen korrigiert`);
                            loadExams();
                          } else {
                            toast.info('Alle Prüfungsnamen sind bereits korrekt');
                          }
                        } else {
                          toast.error('Fehler beim Korrigieren der Namen');
                        }
                      } catch (error) {
                        toast.error('Fehler beim Korrigieren der Namen');
                      }
                    }}
                    data-testid="fix-exam-names-btn"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" />
                    Namen korrigieren
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {exams.map(exam => (
                    <div key={exam.exam_id} className="p-3 border rounded-lg flex justify-between items-center">
                      <div className="flex-1">
                        <span className="font-medium">{exam.exam_id}</span>
                        <span className="text-gray-500 ml-2">- {exam.name}</span>
                      </div>
                      <div className="flex gap-4 items-center">
                        <div className="flex gap-2 text-sm text-gray-500">
                          <span>{exam.aku_count} AKUs</span>
                          <span>{exam.question_count} Fragen</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            const newName = prompt(`Neuer Name für ${exam.exam_id}:`, exam.name);
                            if (newName && newName !== exam.name) {
                              try {
                                const response = await fetch(`${API}/question-engine/exams/${exam.exam_id}`, {
                                  method: 'PUT',
                                  headers: {
                                    ...getAuthHeaders(),
                                    'Content-Type': 'application/json'
                                  },
                                  body: JSON.stringify({ name: newName })
                                });
                                if (response.ok) {
                                  toast.success(`Prüfung umbenannt: ${newName}`);
                                  fetchData();
                                } else {
                                  const error = await response.json();
                                  toast.error(error.detail || 'Fehler beim Umbenennen');
                                }
                              } catch (error) {
                                toast.error('Fehler beim Umbenennen');
                              }
                            }
                          }}
                        >
                          Umbenennen
                        </Button>
                        {exam.aku_count === 0 && exam.question_count === 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={async () => {
                              if (confirm(`Prüfung "${exam.exam_id}" wirklich löschen?`)) {
                                try {
                                  const response = await fetch(`${API}/question-engine/exams/${exam.exam_id}`, {
                                    method: 'DELETE',
                                    headers: getAuthHeaders()
                                  });
                                  if (response.ok) {
                                    toast.success(`Prüfung ${exam.exam_id} gelöscht`);
                                    fetchData();
                                  } else {
                                    const error = await response.json();
                                    toast.error(error.detail || 'Fehler beim Löschen');
                                  }
                                } catch (error) {
                                  toast.error('Fehler beim Löschen');
                                }
                              }
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Exam Config Tab - Under Advanced Diagnose */}
        <TabsContent value="config" className="space-y-4">
          {activeSubTab === 'config' && (
            <ExamConfigManager key="config-tab" getAuthHeaders={getAuthHeaders} />
          )}
        </TabsContent>

        {/* Source Registry Tab */}
        <TabsContent value="sources" className="space-y-4">
          {activeSubTab === 'sources' && (
            <SourceRegistryManager 
              key={`sources-${selectedExam}`}
              isActive={true} 
              onNavigateToEvidence={handleNavigateToEvidence}
              onNavigateToChunks={handleNavigateToChunks}
            />
          )}
        </TabsContent>

        {/* Ontology Graph Tab - Includes Proposals, Versions, and AKU V2 */}
        <TabsContent value="ontology" className="space-y-4">
          {activeSubTab === 'ontology' && (
            <OntologyManager key={`ontology-${selectedExam}`} isActive={true} examProfileId={selectedExam} />
          )}
        </TabsContent>

        {/* Maintenance moved to Admin Panel main tab */}
      </Tabs>
    </div>
  );
};

export default AdminQuestionEngineTab;
